import { useState, useCallback, useEffect } from 'react';
import { apiService } from '../services/api';
import { useToastNotifications } from '../components/ui/Toast';

const normalizeTrialPayload = (payload: any) => {
  if (!payload || typeof payload !== 'object') return null;

  const username = String(payload.username || '').trim();
  const password = String(payload.password || '').trim();
  if (!username || !password) return null;

  return {
    username,
    password,
    expDate: payload.expDate || payload.exp_date || undefined,
    duration:
      payload.duration != null && Number.isFinite(Number(payload.duration))
        ? Number(payload.duration)
        : undefined
  };
};

/**
 * Mesma lógica do “Teste Rápido HTTP” do dashboard: gera trial via API e expõe credenciais no modal.
 */
export function useQuickTrialHttp() {
  const { showSuccess, showError } = useToastNotifications();
  const [trialPackageId, setTrialPackageId] = useState(194);
  const [generatingTrial, setGeneratingTrial] = useState(false);
  const [lastGeneratedTrial, setLastGeneratedTrial] = useState<any>(null);

  useEffect(() => {
    const fetchTrialSettings = async () => {
      try {
        const data = await apiService.getTrialGeneratorSettings();
        if (data.success && data.data != null && data.data.xuiTrialPackageId != null) {
          const id = Number(data.data.xuiTrialPackageId);
          if (Number.isFinite(id)) setTrialPackageId(id);
        }
      } catch {
        // silencioso
      }
    };
    fetchTrialSettings();
  }, []);

  const handleGenerateTestAutomations = useCallback(async () => {
    try {
      setGeneratingTrial(true);
      const settingsData = await apiService.getTrialGeneratorSettings();
      if (settingsData.success && settingsData.data?.xuiTrialPackageId != null) {
        const id = Number(settingsData.data.xuiTrialPackageId);
        if (Number.isFinite(id)) setTrialPackageId(id);
      }

      const packageId = settingsData?.data?.xuiTrialPackageId ?? trialPackageId ?? 194;
      const data = await apiService.generateTrial(packageId, 6, 1, { quickTest: true });

      if (data.success) {
        const trialData = normalizeTrialPayload(data.data) || normalizeTrialPayload(data);
        if (!trialData) {
          showError(
            'Erro ao gerar teste',
            'Credenciais não retornadas pelo servidor. Verifique as configurações do gerador de testes.'
          );
          return;
        }
        setLastGeneratedTrial(trialData);
        showSuccess('Teste gerado com sucesso!', 'Confira o modal com as credenciais e mensagem para copiar');
      } else if (data.needsSetup) {
        showError(
          'Configuração necessária',
          'Configure seu token XUI nas configurações ou use um painel cujo dono tenha token cadastrado.'
        );
      } else {
        showError('Erro ao gerar teste', data.message || data.error || 'Não foi possível gerar o teste');
      }
    } catch (error: any) {
      const errMsg =
        error.response?.data?.error || error.response?.data?.message || error.message || 'Erro ao gerar trial';
      showError('Erro ao gerar teste', errMsg);
    } finally {
      setGeneratingTrial(false);
    }
  }, [trialPackageId, showSuccess, showError]);

  return {
    generatingTrial,
    lastGeneratedTrial,
    setLastGeneratedTrial,
    handleGenerateTestAutomations
  };
}
